import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, Observable, startWith, switchMap, take } from 'rxjs';
import {
  ConfirmationDialogComponent,
  IConfirmationDialogConfig,
} from '../../shared/components/confirmation-dialog/confirmation-dialog.component';

export interface IBeforeUnloadParameters {
  shouldNavigationBeIntervened: () => boolean;
  modalData: () => IConfirmationDialogConfig;
  isActionTakenCompleted$: Observable<unknown>;
  confirmationModalTruePayloadCallback: () => unknown;
}

/**
 * @description
 * Is used for preventing navigation if necessary, for example having unsaved changes in form
 * On navigation attempt, {@link shouldNavigationBeIntervened} will be called to confirm whether the condition to stop the navigation is enabled at that moment
 * If it is enabled, a confirmation modal will be opened with its data based on the result of {@link modalData} so the modal content can be determined at the navigation moment
 * In confirmation modal, clicking on cancel will yield undefined, don't submit will yield false and submit will yield true.
 * If confirmation modal yields true, {@link confirmationModalTruePayloadCallback} will be called to take an action (for example store action dispatch)
 * {@link isActionTakenCompleted$} will be used to determine when the operation done on {@link confirmationModalTruePayloadCallback} is completed (for example request status is set to success).
 * If there is no delay, new Observable<boolean | undefined>().pipe(startWith(true)) can be used to continue without delay
 *
 * If navigation prevention depends on a modal's content, closeOnNavigation: false, must be provided so navigation attempt doesn't close that modal before navigation prevention code can run.
 * After navigation is decided to be done, the modals will be closed in {@link CanDeactivateGuard}
 * Preventing the refresh and closing the window is not done here, @HostListener('window:beforeunload', ['$event']) can be used in the component to prevent them
 *
 * Note that this is a providedIn: 'root' injected service, so its data must be managed carefully.
 * For example destroyObs$ in {@link setUnloadPrevention} is used so the parameters provided from another page can't remain active after navigation
 */
@Injectable({ providedIn: 'root' })
export abstract class NavigationInterventionService {
  private readonly unsetNavigationIntervention$ = new Observable<boolean | undefined>().pipe(startWith(false));
  private currentNavigationIntervention$ = this.unsetNavigationIntervention$;
  private readonly matDialog = inject(MatDialog);

  public setUnloadPrevention(parameters: IBeforeUnloadParameters, destroyObs$: Observable<unknown>): void {
    destroyObs$.pipe(take(1)).subscribe(() => {
      this.currentNavigationIntervention$ = this.unsetNavigationIntervention$;
    });

    this.currentNavigationIntervention$ = new Observable().pipe(
      startWith(null),
      switchMap(() => {
        return parameters.shouldNavigationBeIntervened()
          ? this.matDialog
              .open(ConfirmationDialogComponent, {
                data: parameters.modalData(),
              })
              .afterClosed()
              .pipe(
                switchMap((modalResult) => {
                  if (modalResult) {
                    parameters.confirmationModalTruePayloadCallback();
                  }

                  return modalResult
                    ? parameters.isActionTakenCompleted$.pipe(map(() => true))
                    : new Observable().pipe(startWith(modalResult));
                }),
              )
          : new Observable().pipe(startWith(false));
      }),
      take(1),
    );
  }

  public getNavigationInterventionObservable(): Observable<boolean | undefined> {
    return this.currentNavigationIntervention$;
  }
}
